<template>
  <div class="apartment-map">
    <button v-if="showToggleButton && mapLoaded" class="toggle-map" @click="toggleMapWidth()">
      <i class="toggle-icon icon-chevron-left"></i>
    </button>
    <div :id="mapElementId" class="map-container"></div>
    <div class="map-attribution">
      <span>{{ attributionText }}</span>
      <span>Powered by Esri</span>
    </div>
    <loading v-if="mapUpdating"></loading>
  </div>
</template>

<script>
import config from '@/config';
import utils from '@/app/utils/utils';
import { debounce } from 'lodash-es';
import Loading from '@/app/components/loading/loading.vue';
import { nextTick } from 'vue';
import useSafeRoute from '@/app/composables/useSafeRoute';

export default {
  components: { Loading },
  name: 'apartment-map',
  setup() {
    const route = useSafeRoute();
    return { route };
  },
  props: {
    showToggleButton: {
      type: Boolean,
      required: true,
    },
    singleListingMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    listings: {
      type: [Array],
      required: true,
    },
  },
  data() {
    return {
      showMap: true,
      mapboxgl: null,
      map: {},
      mapLoaded: false,
      mapElementId: `map-${utils.newId()}`,
      openPopupId: null,
      mapUpdating: true,
      geojson: null,
      attributionJson: null,
      listingsInClusters: {},
      highlightedStateId: null,
      lastSetZoom: null,
      highlightedClusterId: null,
      markerPopup: null,
      isMobile: false,
      mapConfig: {
        defaultCenter: [26.561362, 64.802149], // somewhere in the middle of Finland
        defaultZoom: 5,
        singleListingZoom: 15,
        minZoom: 4,
        maxZoom: 18,
        clusterMaxZoom: 16,
        clusterRadius: 40,
        layerNames: {
          clusters: 'clusters',
          unClusteredPoints: 'unclustered-points',
          markerHighlights: 'marker-highlights',
          clusterHighlights: 'cluster-highlights',
        },
      },
    };
  },
  watch: {
    mapLoaded() {
      this.updateMap();
    },
    listings() {
      this.updateMap();
    },
    selectedMobileTab(tabName) {
      if (tabName === config.MOBILE_TABS.MAP) {
        this.reInitMapInMobile();
      }
    },
  },
  methods: {
    reInitMapInMobile() {
      this.map.resize();
      this.updateMap();
      if (this.markerPopup) {
        this.markerPopup.remove();
      }
    },
    updateMap() {
      if (!this.mapLoaded) {
        return;
      }

      this.mapUpdating = true;

      this.updateGeojson();
      this.clearLayers();
      this.createLayers();
      this.fitMapToMarkers();

      // Close popup if the listing is not on the result list anymore
      if (this.markerPopup && !this.isIdInListings(this.openPopupId)) {
        this.markerPopup.remove();
      }
    },
    isIdInListings(id) {
      return this.listings.some((listing) => listing.id === id);
    },
    fitMapToMarkers() {
      if (
        this.listings.length > 1 &&
        this.map.getLayer(this.mapConfig.layerNames.clusters) &&
        (this.showMap || this.selectedMobileTab === config.MOBILE_TABS.MAP)
      ) {
        if (!this.lastSetZoom || this.map.getZoom() === this.lastSetZoom) {
          const bounds = new this.mapboxgl.LngLatBounds();
          this.listings.forEach((listing) => {
            const coordinates = listing && listing.location ? listing.location.coordinates : null;
            if (!coordinates || !coordinates.latitude || !coordinates.longitude) {
              return;
            }
            bounds.extend([coordinates.longitude, coordinates.latitude]);
          });
          if (!bounds.isEmpty()) {
            this.map.fitBounds(bounds, { padding: { top: 50, bottom: 80, left: 30, right: 30 } });
            this.lastSetZoom = this.map.getZoom();
          }
        }
      } else if (this.listings.length && this.listings[0].location.coordinates) {
        this.map.setCenter([
          this.listings[0].location.coordinates.longitude,
          this.listings[0].location.coordinates.latitude,
        ]);
        this.map.setZoom(this.mapConfig.singleListingZoom);
      } else {
        this.map.setCenter(this.mapConfig.defaultCenter);
        this.map.setZoom(this.mapConfig.defaultZoom);
      }
      this.mapUpdating = false;
    },
    highlightMarker(listingId) {
      this.unHighlightCurrentMarker();
      if (this.map.getSource('listingsGeoJson')) {
        this.highlightedStateId = parseInt(listingId, 10);
        if (!isNaN(this.highlightedStateId)) {
          this.map.setFeatureState({ source: 'listingsGeoJson', id: this.highlightedStateId }, { highlighted: true });
        }
      }
    },
    unHighlightCurrentMarker() {
      if (this.highlightedStateId) {
        this.map.setFeatureState({ source: 'listingsGeoJson', id: this.highlightedStateId }, { highlighted: false });
        this.highlightedStateId = null;
      }
    },
    async initMap() {
      const mapStyleJson = await import('@/app/assets/world-street-map.json');
      this.mapboxgl = await import('mapbox-gl');
      this.map = new this.mapboxgl.Map({
        container: this.mapElementId,
        style: mapStyleJson,
        maxZoom: this.mapConfig.maxZoom,
        minZoom: this.mapConfig.minZoom,
        center: this.mapConfig.defaultCenter,
        pitchWithRotate: false,
      });

      // Removed for now, should be added back later when it works properly
      // this.map.addControl(new this.mapboxgl.FullscreenControl());
      this.map.addControl(new this.mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

      // disable map rotation
      this.map.dragRotate.disable();
      this.map.touchZoomRotate.disableRotation();

      this.map.on('load', () => {
        this.map.addSource('listingsGeoJson', {
          type: 'geojson',
          data: this.geojson,
          cluster: true,
          clusterMaxZoom: this.mapConfig.clusterMaxZoom,
          clusterRadius: this.mapConfig.clusterRadius,
        });

        this.map.resize(); // resize the map after it's ready so it fits it's container

        this.map.on('zoomend', this.removePopupIfMarkerInCluster);

        const images = [
          { url: '/assets/poi_cluster_marker.png', id: 'clusterMarker' },
          { url: '/assets/poi_marker.png', id: 'marker' },
        ];

        Promise.all(
          images.map(
            (img) =>
              new Promise((resolve) => {
                this.map.loadImage(img.url, (error, res) => {
                  this.map.addImage(img.id, res);
                  resolve();
                });
              })
          )
        ).then(() => {
          this.mapLoaded = true;
        });
      });

      this.$bus.$on('hoverSearchResult', (listingId) => {
        if (listingId) {
          this.highlightMarker(listingId);
          this.updateClusterListingIds(() => {
            for (const key of Object.keys(this.listingsInClusters)) {
              if (this.listingsInClusters[key].includes(listingId)) {
                this.highlightedClusterId = key;
                this.map.setFeatureState(
                  { source: 'listingsGeoJson', id: this.highlightedClusterId },
                  { highlighted: true }
                );
              }
            }
          });
        } else {
          if (this.highlightedClusterId) {
            this.map.setFeatureState(
              { source: 'listingsGeoJson', id: this.highlightedClusterId },
              { highlighted: false }
            );
          }
          this.unHighlightCurrentMarker();
        }
      });
    },
    removePopupIfMarkerInCluster() {
      if (this.openPopupId) {
        this.updateClusterListingIds(() => {
          for (const key of Object.keys(this.listingsInClusters)) {
            if (this.listingsInClusters[key].includes(this.openPopupId)) {
              this.markerPopup.remove();
            }
          }
        });
      }
    },
    toggleMapWidth() {
      // resize map *after* container resize animation is finished
      this.showMap = !this.showMap;
      this.$emit('toggleMap', this.showMap);
      setTimeout(() => {
        this.map.resize();
        if (this.showMap) {
          this.fitMapToMarkers();
        }
      }, 310);
    },
    updateGeojson() {
      const features = this.listings
        .filter((listing) => {
          return listing && listing.location && listing.location.coordinates;
        })
        .map((listing) => {
          // prettier-ignore
          return {
            "type": "Feature",
            "geometry": {
              "type": "Point",
              "coordinates": [listing.location.coordinates.longitude, listing.location.coordinates.latitude]
            },
            "properties": {
              "id": listing.id,
            },
            "id": listing.id
          }
        });

      // prettier-ignore
      this.geojson = {
        "name": "NewFeatureType",
        "type": "FeatureCollection",
        "features": features
      };

      this.map.getSource('listingsGeoJson').setData(this.geojson);
    },
    clearLayers() {
      const layers = Object.values(this.mapConfig.layerNames);
      layers.forEach((layer) => {
        if (this.map.getLayer(layer)) {
          this.map.removeLayer(layer);
        }
      });
    },
    createLayers() {
      this.map.addLayer({
        id: this.mapConfig.layerNames.unClusteredPoints,
        type: 'symbol',
        source: 'listingsGeoJson',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': 'marker',
          'icon-size': 0.6,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'bottom',
          'icon-padding': 12,
        },
      });

      if (this.singleListingMode) {
        return;
      }

      this.map.addLayer(
        {
          id: this.mapConfig.layerNames.markerHighlights,
          type: 'circle',
          source: 'listingsGeoJson',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': '#FFFFFF',
            'circle-radius': 23,
            'circle-opacity': ['case', ['boolean', ['feature-state', 'highlighted'], false], 0.6, 0],
            'circle-stroke-opacity': ['case', ['boolean', ['feature-state', 'highlighted'], false], 0.8, 0],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ff6a10',
            'circle-translate': [0, -20],
          },
        },
        this.mapConfig.layerNames.unClusteredPoints
      );

      this.map.addLayer({
        id: this.mapConfig.layerNames.clusters,
        type: 'symbol',
        source: 'listingsGeoJson',
        filter: ['has', 'point_count'],
        layout: {
          'icon-image': 'clusterMarker',
          'icon-size': 0.9,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'text-field': '{point_count}',
          'text-font': ['Arial Unicode MS Bold'],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          'text-size': 13,
          'icon-anchor': 'bottom',
          'text-anchor': 'bottom',
          'text-offset': [0, -1.1],
        },
        paint: {
          'text-color': '#FFFFFF',
        },
      });

      this.map.addLayer(
        {
          id: this.mapConfig.layerNames.clusterHighlights,
          type: 'circle',
          source: 'listingsGeoJson',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': '#FFFFFF',
            'circle-radius': 23,
            'circle-opacity': ['case', ['boolean', ['feature-state', 'highlighted'], false], 0.6, 0],
            'circle-stroke-opacity': ['case', ['boolean', ['feature-state', 'highlighted'], false], 0.8, 0],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ff6a10',
            'circle-translate': [0, -20],
          },
        },
        this.mapConfig.layerNames.clusters
      );

      this.initLayerFunctionalities();
    },
    initLayerFunctionalities() {
      // zoom closer when a cluster icon is clicked
      this.map.on('click', this.mapConfig.layerNames.clusters, (e) => {
        const features = this.map.queryRenderedFeatures(e.point, {
          layers: [this.mapConfig.layerNames.clusters],
        });
        const clusterId = features[0].properties.cluster_id;
        this.map.getSource('listingsGeoJson').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) {
            return;
          }

          this.map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom,
          });
        });
      });

      // When a marker is hovered, a marked highlight (a circle at the same spot but on a different layer)
      // is made visible
      this.map.on('mouseenter', this.mapConfig.layerNames.markerHighlights, (e) => {
        if (e.features.length > 0) {
          this.highlightedStateId = e.features[0].id;
          this.highlightMarker(this.highlightedStateId);
        }
      });

      this.map.on('mouseleave', this.mapConfig.layerNames.markerHighlights, () => {
        this.unHighlightCurrentMarker();
      });

      // Marker click, setting the popup content and opening the popup
      this.map.on('click', this.mapConfig.layerNames.unClusteredPoints, (e) => {
        const listingId = e.features[0].properties.id;
        const selector = `div[data-listing-id="${listingId}"]`;
        const element = document.querySelector(selector).cloneNode(true);
        const href = element.querySelector('a:first-child').getAttribute('href');
        const coordinates = e.features[0].geometry.coordinates.slice();

        // Ensure that if the map is zoomed out such that multiple
        // copies of the feature are visible, the popup appears
        // over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        if (this.isMobile) {
          this.map.panTo(coordinates, { offset: [0, 100] });
        }

        this.$emit('setHighlightedItem', listingId);

        // Popup click, going to listing page
        element.addEventListener('click', (event) => {
          event.preventDefault();
          this.$emit('removeHighlightedItem');
          void this.$router.push(href);
        });

        const markerHeight = 50,
          markerRadius = 30,
          linearOffset = 10;
        const popupOffsets = {
          top: [0, 15],
          'top-left': [0, 0],
          'top-right': [0, 0],
          bottom: [0, -markerHeight],
          'bottom-left': [linearOffset, (markerHeight - markerRadius + linearOffset) * -1],
          'bottom-right': [-linearOffset, (markerHeight - markerRadius + linearOffset) * -1],
          left: [markerRadius, (markerHeight - markerRadius) * -1],
          right: [-markerRadius, (markerHeight - markerRadius) * -1],
        };

        // On bigger screens the popup anchor is not defined, so the popup will open
        // to the direction that it fits to
        // In mobile size, it opens on top of the marker (anchor from the bottom)
        const popupConfig = {
          mobile: { className: 'map-popup', offset: popupOffsets.bottom, anchor: 'bottom' },
          desktop: { className: 'map-popup', offset: popupOffsets },
        };
        const popupConfigObj = this.isMobile ? popupConfig.mobile : popupConfig.desktop;

        if (this.openPopupId !== listingId) {
          this.markerPopup = new this.mapboxgl.Popup(popupConfigObj)
            .setLngLat(coordinates)
            .setDOMContent(element)
            .addTo(this.map);

          this.openPopupId = listingId;

          this.highlightMarker(listingId);

          this.markerPopup.on('close', () => {
            if (this.openPopupId === listingId || !this.openPopupId) {
              this.$emit('removeHighlightedItem');
            }
            this.openPopupId = null;
          });
        }
      });
    },
    updateClusterListingIds(callback) {
      this.listingsInClusters = {};
      // get the features whether or not they are visible
      const features = this.map.querySourceFeatures('listingsGeoJson');
      // loop through each feature
      const promises = [];
      features.forEach((feature) => {
        // get properties, which include the clustered properties
        const props = feature.properties;
        // continue only if the point is part of a cluster
        if (!props.cluster) {
          return;
        }
        // if yes, get the cluster_id
        const id = props.cluster_id;
        this.listingsInClusters[id] = [];

        promises.push(
          new Promise((resolve) => {
            if (this.map.getSource('listingsGeoJson').getClusterLeaves(id)) {
              this.map.getSource('listingsGeoJson').getClusterLeaves(id, 50, 0, (err, aFeatures) => {
                aFeatures.forEach((point) => {
                  if (this.listingsInClusters[id]) {
                    this.listingsInClusters[id].push(point.properties.id);
                    resolve();
                  }
                });
              });
            }
          })
        );
      });

      if (callback) {
        Promise.all(promises).then(callback);
      }
    },
    checkIfMobile() {
      this.isMobile = window.innerWidth < config.BREAKPOINTS.TABLET_PORTRAIT;
    },
  },
  mounted() {
    this.fitMapToMarkers = debounce(this.fitMapToMarkers, 300);
    if (document.getElementById(this.mapElementId)) {
      this.initMap();
    }

    this.$bus.$on('search-selectedMobileTab', (tabName) => {
      if (tabName === config.MOBILE_TABS.MAP) {
        nextTick().then(() => {
          // Workaround to get the map full width
          this.map.resize();
        });
      }
    });

    fetch('https://cdn.arcgis.com/sharing/rest/content/items/692595d4f03a405c9e56ec1039ed63b6/?f=pjson')
      .then((res) => res.json())
      .then((json) => {
        this.attributionJson = json;
      })
      .catch((err) => {
        throw err;
      });

    this.checkIfMobile();

    this.$bus.$on('windowResized', () => {
      this.checkIfMobile();
    });
  },
  beforeUnmount() {
    this.$bus.$off('hoverSearchResult');
    if (this.map) {
      this.map.remove();
    }
  },
  computed: {
    attributionText() {
      return this.attributionJson && this.attributionJson.accessInformation
        ? this.attributionJson.accessInformation
        : '';
    },
  },
};
</script>

<style>
@import 'mapbox-gl/src/css/mapbox-gl.css';
</style>
